import { Dictionary } from '@ngrx/entity';
import { createFeatureSelector, createSelector, MemoizedSelector, Selector } from '@ngrx/store';

import * as fromReducer from './category-airplay-security-profile.reducer';
import * as fromState from './category-airplay-security-profile.state';

import { AppleAirPlaySecurityPreset } from '@rocksoft/mdm-sdk-v2';
import { AirplaySecurityProfileStoreSelectors } from '../airplay-security';
import { Category } from '../router/category';
import { CategoryAirplaySecurityProfileConfiguration } from './category-airplay-security-profile.state';

// ============================================================================
// STATE SELECTORS
// ============================================================================

const selectCategoryAirplaySecurityProfileState = createFeatureSelector<fromState.CategoryAirplaySecurityProfileState>(
  'categoryAirplaySecurityProfile'
);

export const selectAllCategoryAirplaySecurityProfiles: Selector<object, CategoryAirplaySecurityProfileConfiguration[]> =
  createSelector(selectCategoryAirplaySecurityProfileState, fromReducer.selectAllCategoryAirplaySecurityProfiles);

export const selectCategoryAirplaySecurityProfileEntities: Selector<
  object,
  Dictionary<CategoryAirplaySecurityProfileConfiguration>
> = createSelector(selectCategoryAirplaySecurityProfileState, fromReducer.selectCategoryAirplaySecurityProfileEntities);

// ============================================================================
// GENERIC SELECTORS
// ============================================================================

/**
 * Generic selector for airplay security profile configuration linked to a category.
 * Automatically handles router selector mapping.
 *
 * Usage in containers:
 * this.linkedConfiguration$ = this.store.select(selectAirplaySecurityProfileLinkedToCategory(category));
 */
export const selectAirplaySecurityProfileLinkedToCategory = (category: Category) => {
  return createSelector(
    selectCategoryAirplaySecurityProfileEntities,
    (configurations: Dictionary<CategoryAirplaySecurityProfileConfiguration>) => {
      if (!category || !configurations) {
        return undefined;
      }
      // Entity keys are `${type}-${id}-${presetId}`, so we need to find by matching type and id
      const configKey = Object.keys(configurations).find(
        (key) => configurations[key].id === category.categoryId && configurations[key].type === category.categoryType
      );
      return configKey ? configurations[configKey] : undefined;
    }
  );
};

/**
 * Generic selector for airplay security profile preset linked to a category type.
 * Automatically handles router selector mapping.
 *
 * Usage in containers:
 * this.linkedPreset$ = this.store.select(selectAirplaySecurityPresetLinkedToCategory(category));
 */
export const selectAirplaySecurityPresetLinkedToCategory = (
  category: Category
): MemoizedSelector<object, AppleAirPlaySecurityPreset | undefined> => {
  return createSelector(
    selectAirplaySecurityProfileLinkedToCategory(category),
    AirplaySecurityProfileStoreSelectors.selectAirplaySecurityProfileEntities,
    (configuration, presets) =>
      presets && configuration ? presets[configuration.airPlaySecurityProfilePresetId] : undefined
  );
};

/**
 * Generic selector for airplay security profile preset applied to a category type (with inheritance).
 * Location inherits from Brand, Room inherits from Location (or Brand if Location has none).
 *
 * Usage in containers:
 * this.appliedPreset$ = this.store.select(selectAirplaySecurityPresetAppliedToCategory(category));
 */
export const selectAirplaySecurityPresetAppliedToCategory = (
  category: Category
): MemoizedSelector<object, AppleAirPlaySecurityPreset | undefined> => {
  if (category.parent) {
    return createSelector(
      selectAirplaySecurityPresetAppliedToCategory(category.parent),
      selectAirplaySecurityPresetLinkedToCategory(category),
      (parent, current) => current ?? parent
    );
  } else {
    return selectAirplaySecurityPresetLinkedToCategory(category);
  }
};
